import { cn } from '@/lib/utils';
import type { MessageContent } from '@/types/workflow';
import { Link, MousePointer } from 'lucide-react';

interface MessengerPreviewProps {
  contents: MessageContent[];
  /** Compact mode: hide header, typing indicator, and input - just message bubbles */
  compact?: boolean;
}

export function MessengerPreview({ contents, compact = false }: MessengerPreviewProps) {
  if (contents.length === 0) {
    return (
      <div className={cn(
        "flex items-center justify-center bg-linear-to-b from-blue-50 to-blue-100",
        compact ? "min-h-[60px] p-3 rounded-lg" : "h-full p-4"
      )}>
        <div className="text-center text-muted-foreground">
          <p className="text-sm">Sem conteúdo para visualizar</p>
          {!compact && <p className="text-xs mt-1">Adicione mensagens ao fluxo</p>}
        </div>
      </div>
    );
  }

  const messageBubbles = (
    <div className={cn("space-y-2", compact ? "p-2" : "p-4")}>
      {contents.map((content) => (
        <div key={content.id} className="flex justify-start">
          <div className="max-w-[280px]">
            {/* Text Message */}
            {content.type === 'text' && content.text && (
              <div className="bg-blue-500 text-white rounded-2xl px-4 py-2 shadow-xs">
                <p className="text-sm whitespace-pre-wrap wrap-break-word">{content.text}</p>
              </div>
            )}

            {/* Image Message */}
            {content.type === 'image' && content.imageUrl && (
              <div className="rounded-2xl overflow-hidden shadow-lg bg-white">
                <img
                  src={content.imageUrl}
                  alt="Message image"
                  className="w-full object-cover max-h-32"
                />
              </div>
            )}

            {/* Card Message */}
            {content.type === 'card' && (
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
                {content.imageUrl && (
                  <img
                    src={content.imageUrl}
                    alt={content.title}
                    className="w-full h-40 object-cover"
                  />
                )}
                <div className="p-3 space-y-1">
                  {content.title && (
                    <p className="font-semibold text-sm">{content.title}</p>
                  )}
                  {content.subtitle && (
                    <p className="text-xs text-muted-foreground">{content.subtitle}</p>
                  )}
                </div>
              </div>
            )}

            {/* Buttons */}
            {content.buttons && content.buttons.length > 0 && (
              <div className="mt-1 bg-white rounded-2xl overflow-hidden shadow-xs divide-y">
                {content.buttons.map((button) => (
                  <button
                    key={button.id}
                    className={cn(
                      "w-full px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors",
                      button.type === 'url'
                        ? "text-blue-600 hover:bg-blue-50"
                        : "text-gray-700 hover:bg-gray-50"
                    )}
                  >
                    {button.type === 'url' ? (
                      <Link className="w-3.5 h-3.5" />
                    ) : (
                      <MousePointer className="w-3.5 h-3.5" />
                    )}
                    <span>{button.title || 'Botão'}</span>
                  </button>
                ))}
              </div>
            )}

            {!compact && (
              <p className="text-[10px] text-gray-500 mt-1 text-right">Agora</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  if (compact) {
    return (
      <div className="bg-linear-to-b from-blue-50 to-blue-100 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
        {messageBubbles}
      </div>
    );
  }

  return (
    <div className="h-full bg-linear-to-b from-blue-50 to-blue-100 overflow-y-auto">
      {/* Messenger Header */}
      <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center gap-3 shadow-xs z-10">
        <div className="w-10 h-10 rounded-full bg-linear-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold">
          B
        </div>
        <div>
          <p className="font-semibold text-sm">Seu Negócio</p>
          <p className="text-xs text-muted-foreground">Geralmente responde em minutos</p>
        </div>
      </div>

      {messageBubbles}

      {/* Typing indicator */}
      <div className="flex justify-start px-4 pb-2">
        <div className="bg-white rounded-full px-4 py-2 shadow-xs flex items-center gap-1">
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>

      {/* Input area (disabled) */}
      <div className="sticky bottom-0 bg-white border-t px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-gray-100 rounded-full px-4 py-2 text-sm text-gray-400 cursor-not-allowed">
            Mensagem...
          </div>
          <button className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white opacity-50 cursor-not-allowed">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
